import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './HeroVideo.css';

const ESTABLISHED = 2024;
const currentYear = new Date().getFullYear();
const yearsInBusiness = currentYear - ESTABLISHED || 1;

const STATS = [
  { value: '500+',  label: 'Happy Clients' },
  { value: `${yearsInBusiness}+`, label: yearsInBusiness === 1 ? 'Year in Business' : 'Years in Business' },
  { value: '1000+', label: 'Orders Fulfilled' },
];

function HeroVideo() {
  const navigate = useNavigate();
  const [videoSettings, setVideoSettings] = useState({
    youtubeUrl: '',
    tagline: 'High-quality custom prints for clothing, merchandise, and more — crafted with precision and care.',
    enabled: true,
  });
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'settings', 'homepage');
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      setVideoSettings({
        youtubeUrl: data.youtubeUrl || '',
        tagline: data.tagline || 'High-quality custom prints for clothing, merchandise, and more — crafted with precision and care.',
        enabled: data.videoBannerEnabled !== false,
      });
    }, async () => {
      // onSnapshot error fallback — try one-time fetch
      try {
        const snap = await getDoc(ref);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        setVideoSettings({
          youtubeUrl: data.youtubeUrl || '',
          tagline: data.tagline || 'High-quality custom prints for clothing, merchandise, and more — crafted with precision and care.',
          enabled: data.videoBannerEnabled !== false,
        });
      } catch (err) {
        console.error('Failed to load hero video settings:', err);
      }
    });
    return () => unsubscribe();
  }, []);

  // Reset iframe error whenever the URL changes
  useEffect(() => { setIframeError(false); }, [videoSettings.youtubeUrl]);

  const youtubeId = extractYouTubeId(videoSettings.youtubeUrl);

  return (
    <section className="hv-section">
      <div className="hv-inner">

        {/* ── LEFT — Brand & Info ── */}
        <div className="hv-left">

          <div className="hv-badge">
            <span className="hv-badge-dot" />
            Trusted Custom Printing
          </div>

          <h1 className="hv-title">JG<br />Printing</h1>

          <div className="hv-shop-info">
            <div className="hv-info-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              Lower Bicutan, Taguig
            </div>
            <div className="hv-info-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Est. {ESTABLISHED}
            </div>
          </div>

          <p className="hv-desc">{videoSettings.tagline}</p>

          <button className="hv-cta" onClick={() => navigate('/clothing')}>
            Shop Now
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>

          {/* Stats strip */}
          <div className="hv-stats">
            {STATS.map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <div className="hv-stats-divider" />}
                <div className="hv-stat">
                  <span className="hv-stat-value">{s.value}</span>
                  <span className="hv-stat-label">{s.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── RIGHT — YouTube embed or fallback ── */}
        {(videoSettings.enabled && youtubeId) && (
          <div className="hv-right">
            <div className="hv-frame">
              {iframeError ? (
                /* ── Offline / blocked fallback ── */
                <div className="hv-frame-fallback">
                  {/* Printer SVG icon */}
                  <svg className="hv-fallback-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="12" y="22" width="40" height="26" rx="4" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.25)" strokeWidth="2"/>
                    <rect x="20" y="10" width="24" height="14" rx="2" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.25)" strokeWidth="2"/>
                    <rect x="20" y="36" width="24" height="10" rx="2" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
                    <circle cx="44" cy="31" r="2.5" fill="rgba(255,255,255,0.35)"/>
                  </svg>
                  <p className="hv-fallback-brand">JG Printing</p>
                  <p className="hv-fallback-sub">Video unavailable — no internet connection</p>
                  <a
                    className="hv-fallback-link"
                    href={`https://www.youtube.com/watch?v=${youtubeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch on YouTube →
                  </a>
                </div>
              ) : (
                <iframe
                  className="hv-iframe"
                  src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&modestbranding=1&rel=0`}
                  title="JG Printing"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  onError={() => setIframeError(true)}
                />
              )}
            </div>
            <div className="hv-frame-accent" />
          </div>
        )}

      </div>
    </section>
  );
}

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export default HeroVideo;